import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Printer, ArrowLeft, FloppyDisk, Check, ArrowClockwise, Trash, User, ClockCounterClockwise, FilePdf } from "@phosphor-icons/react";
import InvoicedToggle from "@/components/InvoicedToggle";

export default function InterventionDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [it, setIt] = useState(null);
  useDocumentTitle(it ? `Intervento ${it.code}` : "Intervento");
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});
  const [customerHistory, setCustomerHistory] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/interventions/${id}`);
      setIt(data);
      setEdit({
        work_description: data.work_description || "",
        tech_notes: data.tech_notes || "",
        price_estimate: data.price_estimate ?? "",
        price_final: data.price_final ?? "",
        cost: data.cost ?? "",
        product_brand: data.product_brand || "",
        product_model: data.product_model || "",
        serial_number: data.serial_number || "",
        accessories: data.accessories || "",
      });
      const hist = await api.get(`/customers/${data.customer_id}/interventions`);
      setCustomerHistory(hist.data.filter((h) => h.id !== data.id));
    } catch (e) {
      toast.error("Intervento non trovato");
      nav("/");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/interventions/${id}`, payload);
      setIt(data);
      toast.success("Salvato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const save = () =>
    patch({
      ...edit,
      price_estimate: edit.price_estimate === "" ? null : parseFloat(edit.price_estimate),
      price_final: edit.price_final === "" ? null : parseFloat(edit.price_final),
      cost: edit.cost === "" ? null : parseFloat(edit.cost),
    });

  const toggleStatus = () => patch({ status: it.status === "open" ? "closed" : "open" });

  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo intervento?")) return;
    await api.delete(`/interventions/${id}`);
    toast.success("Intervento eliminato");
    nav("/");
  };

  if (!it) return <div className="p-10 text-muted-foreground">Caricamento…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4 no-print"
              style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft size={14} /> Indietro
      </button>

      <header className="flex flex-col lg:flex-row justify-between gap-4 mb-8 no-print">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// SCHEDA</div>
          <h1 className="text-4xl font-display font-bold tracking-tight font-mono" data-testid="intervention-code">{it.code}</h1>
          <div className="flex items-center gap-3 mt-3">
            <Badge className={`font-mono text-[10px] uppercase border ${
              it.status === "open"
                ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15"
                : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/15"
            }`} data-testid="intervention-status">
              {it.status === "open" ? "Aperto" : "Chiuso"}
            </Badge>
            <span className="font-mono text-xs text-muted-foreground">
              Ingresso: {new Date(it.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              {it.closed_at && (
                <> · Chiusura: {new Date(it.closed_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
              )}
            </span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <InvoicedToggle intervention={it} onChange={setIt} size="lg" />
            {it.invoiced_at && (
              <span className="font-mono text-[10px] text-muted-foreground">
                emessa il {new Date(it.invoiced_at).toLocaleDateString("it-IT")}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/interventi/${id}/etichetta`}>
            <Button variant="outline" data-testid="print-label-btn" className="rounded-full gap-2">
              <Printer size={16} /> Stampa etichetta
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await api.get(`/interventions/${id}/pdf`, { responseType: "blob" });
                const url = window.URL.createObjectURL(res.data);
                window.open(url, "_blank");
              } catch {
                toast.error("Errore generazione PDF");
              }
            }}
            data-testid="download-pdf-btn"
            className="rounded-full gap-2"
          >
            <FilePdf size={16} /> Ricevuta PDF
          </Button>
          <Button onClick={toggleStatus} disabled={saving} variant={it.status === "open" ? "default" : "outline"}
                  data-testid="toggle-status-btn" className="rounded-full gap-2">
            {it.status === "open" ? <><Check size={16} weight="bold" /> Chiudi</> : <><ArrowClockwise size={16} /> Riapri</>}
          </Button>
          <Button onClick={remove} variant="ghost" data-testid="delete-btn" className="rounded-full gap-2 text-destructive hover:text-destructive">
            <Trash size={16} />
          </Button>
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: details editor */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-border/70 rounded-lg">
            <h2 className="font-display font-bold tracking-tight mb-4 text-lg">Prodotto</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</Label>
                <div className="mt-1.5 py-2 px-3 rounded-md bg-secondary/60 text-sm">{it.product_type}</div>
              </div>
              <Field label="Marca">
                <Input value={edit.product_brand} onChange={(e) => setEdit({ ...edit, product_brand: e.target.value })} data-testid="edit-brand" />
              </Field>
              <Field label="Modello">
                <Input value={edit.product_model} onChange={(e) => setEdit({ ...edit, product_model: e.target.value })} data-testid="edit-model" />
              </Field>
              <Field label="Numero seriale">
                <Input value={edit.serial_number} onChange={(e) => setEdit({ ...edit, serial_number: e.target.value })} className="font-mono" data-testid="edit-serial" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Accessori">
                  <Input value={edit.accessories} onChange={(e) => setEdit({ ...edit, accessories: e.target.value })} data-testid="edit-accessories" />
                </Field>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-border/70 rounded-lg">
            <h2 className="font-display font-bold tracking-tight mb-4 text-lg">Lavoro</h2>
            <div className="space-y-4">
              <Field label="Descrizione lavoro">
                <Textarea rows={4} value={edit.work_description} onChange={(e) => setEdit({ ...edit, work_description: e.target.value })} data-testid="edit-work" />
              </Field>
              <Field label="Note tecniche / diagnosi">
                <Textarea rows={4} value={edit.tech_notes} onChange={(e) => setEdit({ ...edit, tech_notes: e.target.value })} data-testid="edit-tech-notes" />
              </Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Preventivo (€)">
                  <Input type="number" step="0.01" value={edit.price_estimate} onChange={(e) => setEdit({ ...edit, price_estimate: e.target.value })} className="font-mono" data-testid="edit-price-estimate" />
                </Field>
                <Field label="Prezzo finale (€)">
                  <Input type="number" step="0.01" value={edit.price_final} onChange={(e) => setEdit({ ...edit, price_final: e.target.value })} className="font-mono" data-testid="edit-price-final" />
                </Field>
                <Field label="Costo sostenuto (€)">
                  <Input type="number" step="0.01" value={edit.cost} onChange={(e) => setEdit({ ...edit, cost: e.target.value })} className="font-mono" data-testid="edit-cost" />
                </Field>
              </div>
              <div className="flex justify-end">
                <Button onClick={save} disabled={saving} data-testid="save-btn" className="rounded-full gap-2">
                  <FloppyDisk size={16} weight="bold" /> {saving ? "Salvo…" : "Salva modifiche"}
                </Button>
              </div>
            </div>
          </Card>

          {customerHistory.length > 0 && (
            <Card className="p-6 border-border/70 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <ClockCounterClockwise size={18} weight="duotone" className="text-primary" />
                <h2 className="font-display font-bold tracking-tight text-lg">Altri interventi del cliente</h2>
              </div>
              <div className="divide-y divide-border/50">
                {customerHistory.map((h) => (
                  <Link
                    key={h.id}
                    to={`/interventi/${h.id}`}
                    data-testid={`history-item-${h.code}`}
                    className="flex items-center justify-between py-3 hover:bg-secondary/40 rounded-md px-2 -mx-2"
                    style={{ transition: "background-color 0.15s ease" }}
                  >
                    <div>
                      <div className="font-mono text-xs text-primary">{h.code}</div>
                      <div className="text-sm mt-0.5">{h.product_type} — {h.work_description.slice(0, 50)}{h.work_description.length > 50 ? "…" : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-xs text-muted-foreground">
                        {new Date(h.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <Badge className={`mt-1 font-mono text-[10px] uppercase border ${
                        h.status === "open"
                          ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15"
                          : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/15"
                      }`}>{h.status === "open" ? "Aperto" : "Chiuso"}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right: QR + customer */}
        <div className="space-y-6">
          <Card className="p-6 border-border/70 rounded-lg">
            <h2 className="font-display font-bold tracking-tight mb-4 text-lg">Codice QR</h2>
            <div className="bg-white p-4 rounded-md border border-border/60 grid place-items-center">
              <QRCodeSVG value={it.code} size={200} level="H" />
            </div>
            <div className="mt-3 font-mono text-center text-sm tracking-widest" data-testid="qr-code-value">{it.code}</div>
            <Link to={`/interventi/${id}/etichetta`}>
              <Button variant="outline" className="w-full mt-4 rounded-full gap-2" data-testid="qr-print-btn">
                <Printer size={16} /> Stampa etichetta
              </Button>
            </Link>
          </Card>

          <Card className="p-6 border-border/70 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <User size={18} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold tracking-tight text-lg">Cliente</h2>
            </div>
            <div className="space-y-2 text-sm">
              <div><span className="font-mono text-[10px] uppercase text-muted-foreground block">Nominativo</span>
                <span className="font-medium">{it.customer_surname} {it.customer_name}</span></div>
              <div><span className="font-mono text-[10px] uppercase text-muted-foreground block">Telefono</span>
                <span className="font-mono">{it.customer_phone}</span></div>
            </div>
            <Separator className="my-4" />
            <Link to={`/clienti/${it.customer_id}`} className="text-sm text-primary hover:underline" data-testid="view-customer-link">
              Vedi scheda cliente →
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
